import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import type { FabColumn, FabFilters, FabScholarshipRow } from '@/lib/types';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Button, Checkbox, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import type { FabFilterState } from './fab-filter-state';
import { formatCount, formatRate, stageRate, type FabOverviewData } from './use-fab-data';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiError,
  PiLoading,
  PiSection,
  PiSortHeader,
  PiStat,
  PiStatGrid,
} from './pi-kit';

/*
 * SEED FAB → Scholarships tab. Reads the shared overview fetch (`useFabOverview`
 * on the page); this tab never fetches.
 *
 * Layout (Product Insights template, 2026-10-19): headline tiles → one table
 * section (desktop table + stacked cards below md) → totals row.
 *
 * - Rows: Scholarship, Applications, share of the filtered total (bar + %).
 * - "Break down by region" adds one column per `data.scholarships.columns`.
 *   ⚠️ COLUMN KEYS ARE POSITIONAL (`r0`, `c0`…): the same key names a different
 *   region under different filters, so cells are keyed by index and a sort on a
 *   breakdown column is never remembered. Only the Scholarship / Applications
 *   sorts are remembered — they mean the same thing under every filter. For the
 *   same reason there is no ColumnManager here: a remembered hidden key would
 *   hide a different region under other filters. The region toggle is the
 *   column choice.
 * - Search (`state.search`, the page toolbar) filters client-side by name.
 * - "Hide zero rows" (default ON) is a view choice; the hidden count is shown
 *   as a fact. Neither search nor hide-zero changes the totals row or the
 *   headline tiles, which are upstream's figures for the current filters (the
 *   share denominator too).
 * - Scholarships carry no company, so names are plain text.
 * - Past PAGE_SIZE rows the list grows with "Show more".
 */

type BaseSortColumn = 'label' | 'total';
type SortDir = 'asc' | 'desc';
interface BaseSort {
  column: BaseSortColumn;
  dir: SortDir;
}
interface ColumnSort {
  index: number;
  dir: SortDir;
  columnsKey: string;
}

const DEFAULT_SORT: BaseSort = { column: 'total', dir: 'desc' };
const PAGE_SIZE = 50;

function parseBaseSort(raw: unknown): BaseSort | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { column, dir } = raw as { column?: unknown; dir?: unknown };
  if (column !== 'label' && column !== 'total') return undefined;
  if (dir !== 'asc' && dir !== 'desc') return undefined;
  return { column, dir };
}

function valueAt(row: FabScholarshipRow, column: FabColumn | undefined): number | undefined {
  return column === undefined ? undefined : row.values[column.key];
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'The scholarships could not be loaded.';
}

/** Share of the filtered total: bar (decorative) + percentage (text). */
function ShareCell({ value, total }: { value: number; total: number }): ReactElement {
  const width = total > 0 && value > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div
        aria-hidden="true"
        className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-xs bg-surface-sunken"
      >
        <div className="h-full rounded-xs bg-chart-1" style={{ width: `${width}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-xs text-fg-secondary tabular-nums">
        {formatRate(stageRate(value, total))}
      </span>
    </div>
  );
}

export function FabScholarshipsTab(props: {
  data: FabOverviewData | null;
  loading: boolean;
  error: unknown | null;
  state: FabFilterState;
  onStateChange(next: FabFilterState): void;
  filters: FabFilters | null;
}): ReactElement {
  const { data, loading, error, state, onStateChange } = props;
  const summary = data?.scholarships ?? null;
  const hasError = error !== null && error !== undefined;

  const [baseSort, setBaseSort] = useRememberedState<BaseSort>(
    'fab-scholarships-tab-sort',
    DEFAULT_SORT,
    parseBaseSort,
  );
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const [hideZero, setHideZero] = useRememberedState('fab-scholarships-tab-hide-zero', true);
  const [byRegion, setByRegion] = useRememberedState('fab-scholarships-tab-by-region', false);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const allColumns = useMemo(() => summary?.columns ?? [], [summary]);
  const columns = useMemo(() => (byRegion ? allColumns : []), [byRegion, allColumns]);

  // A breakdown-column sort names a position; new columns make it meaningless,
  // so the sort carries the column set it was made under and lapses otherwise.
  const columnsKey = columns.map((c) => `${c.key}:${c.label}`).join('|');
  const effectiveColumnSort =
    columnSort !== null &&
    columnSort.columnsKey === columnsKey &&
    columnSort.index < columns.length
      ? columnSort
      : null;

  const searchActive = state.search.trim() !== '';
  const needle = state.search.trim().toLocaleLowerCase();

  const { rows, hiddenCount } = useMemo(() => {
    const all = summary?.rows ?? [];
    const matched =
      needle === '' ? all : all.filter((row) => row.displayName.toLocaleLowerCase().includes(needle));
    const kept = hideZero ? matched.filter((row) => row.total !== 0) : matched;
    const byName = (a: FabScholarshipRow, b: FabScholarshipRow) =>
      a.displayName.localeCompare(b.displayName);
    const sorted = [...kept];
    if (effectiveColumnSort !== null) {
      const column = columns[effectiveColumnSort.index];
      const sign = effectiveColumnSort.dir === 'asc' ? 1 : -1;
      sorted.sort((a, b) => {
        const av = valueAt(a, column);
        const bv = valueAt(b, column);
        // Missing values sit last in either direction.
        if (av === undefined && bv === undefined) return byName(a, b);
        if (av === undefined) return 1;
        if (bv === undefined) return -1;
        return av === bv ? byName(a, b) : (av - bv) * sign;
      });
    } else if (baseSort.column === 'label') {
      const sign = baseSort.dir === 'asc' ? 1 : -1;
      sorted.sort((a, b) => byName(a, b) * sign);
    } else {
      const sign = baseSort.dir === 'asc' ? 1 : -1;
      sorted.sort((a, b) => (a.total === b.total ? byName(a, b) : (a.total - b.total) * sign));
    }
    return { rows: sorted, hiddenCount: matched.length - kept.length };
  }, [summary, needle, hideZero, effectiveColumnSort, baseSort, columns]);

  /* -- headline -------------------------------------------------------- */

  const headline = useMemo(() => {
    if (summary === null) return null;
    const grandTotal = summary.totals.total;
    const withApplications = summary.rows.filter((row) => row.total > 0).length;
    let top: FabScholarshipRow | null = null;
    for (const row of summary.rows) {
      if (row.total > 0 && (top === null || row.total > top.total)) top = row;
    }
    return { grandTotal, withApplications, listed: summary.rows.length, top };
  }, [summary]);

  /* -- sorting --------------------------------------------------------- */

  type SortTarget = BaseSortColumn | number;

  const sortStateOf = (target: SortTarget): SortDir | null => {
    if (typeof target === 'number') {
      return effectiveColumnSort !== null && effectiveColumnSort.index === target
        ? effectiveColumnSort.dir
        : null;
    }
    return effectiveColumnSort === null && baseSort.column === target ? baseSort.dir : null;
  };

  const ariaSortOf = (target: SortTarget): 'ascending' | 'descending' | undefined => {
    const current = sortStateOf(target);
    return current === null ? undefined : current === 'asc' ? 'ascending' : 'descending';
  };

  const onHeaderClick = (target: SortTarget) => {
    const current = sortStateOf(target);
    const firstDir: SortDir = target === 'label' ? 'asc' : 'desc';
    const nextDir: SortDir = current === null ? firstDir : current === 'asc' ? 'desc' : 'asc';
    if (typeof target === 'number') {
      setColumnSort({ index: target, dir: nextDir, columnsKey });
    } else {
      setColumnSort(null);
      setBaseSort({ column: target, dir: nextDir });
    }
  };

  const sortHeader = (target: SortTarget, label: string, align: 'left' | 'right') => {
    const current = sortStateOf(target);
    return (
      <PiSortHeader
        label={label}
        active={current !== null}
        direction={current ?? (target === 'label' ? 'asc' : 'desc')}
        onClick={() => onHeaderClick(target)}
        align={align}
      />
    );
  };

  /* -- controls -------------------------------------------------------- */

  const controls = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {hideZero && hiddenCount > 0 && (
        <span className="text-xs text-fg-muted tabular-nums">
          {formatCount(hiddenCount)} hidden
        </span>
      )}
      <Checkbox
        label="Hide zero rows"
        checked={hideZero}
        onChange={(event) => setHideZero(event.target.checked)}
      />
      <Checkbox
        label="Break down by region"
        checked={byRegion}
        disabled={summary !== null && allColumns.length === 0}
        onChange={(event) => setByRegion(event.target.checked)}
      />
    </div>
  );

  /* -- body ------------------------------------------------------------ */

  const visible = rows.slice(0, limit);
  const remaining = rows.length - visible.length;

  let body: ReactNode;
  if (hasError && summary === null) {
    body = (
      <div className="p-4">
        <PiError message={errorMessage(error)} />
      </div>
    );
  } else if (summary === null) {
    body = loading ? (
      <PiLoading variant="table" rows={8} />
    ) : (
      <PiEmpty title="No scholarships match these filters." />
    );
  } else if (rows.length === 0) {
    body = (
      <PiEmpty
        title="No scholarships match these filters."
        action={
          searchActive ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onStateChange({ ...state, search: '' })}
            >
              Clear search
            </Button>
          ) : hideZero && hiddenCount > 0 ? (
            <Button size="sm" variant="secondary" onClick={() => setHideZero(false)}>
              Show zero rows
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    const totals = summary.totals;
    const grandTotal = totals.total;
    body = (
      <div
        className={cx('min-w-0', loading && 'opacity-60 transition-opacity motion-reduce:transition-none')}
        aria-busy={loading}
      >
        {hasError && (
          <div className="px-4 pt-3">
            <PiError message={errorMessage(error)} />
          </div>
        )}

        {/* Desktop */}
        <div className="max-md:hidden">
          <TableWrap frozenHeader>
            <Table>
              <THead>
                <TR>
                  <TH
                    frozen="left"
                    frozenOffset={0}
                    aria-sort={ariaSortOf('label')}
                    className="min-w-56"
                  >
                    {sortHeader('label', 'Scholarship', 'left')}
                  </TH>
                  <TH numeric aria-sort={ariaSortOf('total')} className="min-w-28 whitespace-nowrap">
                    {sortHeader('total', 'Applications', 'right')}
                  </TH>
                  <TH className="min-w-44">Share</TH>
                  {columns.map((column, index) => (
                    <TH
                      key={index}
                      numeric
                      aria-sort={ariaSortOf(index)}
                      className={cx('min-w-24 whitespace-nowrap', index === 0 && 'border-l border-border')}
                    >
                      {sortHeader(index, column.label, 'right')}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {visible.map((row) => (
                  <TR key={row.scholarshipId}>
                    <TH
                      scope="row"
                      frozen="left"
                      frozenOffset={0}
                      className="max-w-80 min-w-56 font-medium whitespace-normal text-fg [overflow-wrap:anywhere]"
                    >
                      {row.displayName}
                    </TH>
                    <TD numeric className="font-medium whitespace-nowrap tabular-nums">
                      {formatCount(row.total)}
                    </TD>
                    <TD>
                      <ShareCell value={row.total} total={grandTotal} />
                    </TD>
                    {columns.map((column, index) => (
                      <TD
                        key={index}
                        numeric
                        className={cx('whitespace-nowrap tabular-nums', index === 0 && 'border-l border-border')}
                      >
                        {formatCount(valueAt(row, column))}
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
              <TBody>
                <TR className="border-t border-hairline-strong">
                  <TH scope="row" frozen="left" frozenOffset={0}>
                    <span className="text-sm font-semibold text-fg">Total</span>
                  </TH>
                  <TD numeric className="font-semibold whitespace-nowrap tabular-nums">
                    {formatCount(grandTotal)}
                  </TD>
                  <TD />
                  {columns.map((column, index) => (
                    <TD
                      key={index}
                      numeric
                      className={cx(
                        'font-semibold whitespace-nowrap tabular-nums',
                        index === 0 && 'border-l border-border',
                      )}
                    >
                      {formatCount(totals.values[column.key])}
                    </TD>
                  ))}
                </TR>
              </TBody>
            </Table>
          </TableWrap>
        </div>

        {/* Mobile */}
        <PiCardList
          items={visible}
          getKey={(row) => row.scholarshipId}
          render={(row) => (
            <div className="grid gap-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                  {row.displayName}
                </span>
                <span className="shrink-0 text-sm font-medium text-fg tabular-nums">
                  {formatCount(row.total)}
                </span>
              </div>
              <ShareCell value={row.total} total={grandTotal} />
              {columns.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {columns.map((column, index) => (
                    <PiCardField
                      key={index}
                      label={column.label}
                      value={<span className="tabular-nums">{formatCount(valueAt(row, column))}</span>}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        />
        <div className="border-t border-border px-4 py-3 md:hidden">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold text-fg">Total</span>
            <span className="shrink-0 text-sm font-semibold text-fg tabular-nums">
              {formatCount(grandTotal)}
            </span>
          </div>
          {columns.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
              {columns.map((column, index) => (
                <PiCardField
                  key={index}
                  label={column.label}
                  value={
                    <span className="font-medium tabular-nums">
                      {formatCount(totals.values[column.key])}
                    </span>
                  }
                />
              ))}
            </div>
          )}
        </div>

        {remaining > 0 && (
          <div className="flex justify-center border-t border-border px-4 py-3">
            <Button
              size="sm"
              variant="secondary"
              className="max-sm:h-11"
              onClick={() => setLimit((n) => n + PAGE_SIZE)}
            >
              Show {formatCount(Math.min(PAGE_SIZE, remaining))} more
            </Button>
          </div>
        )}
      </div>
    );
  }

  const tilesLoading = summary === null && loading;

  return (
    <div className="grid min-w-0 gap-4">
      {!(hasError && summary === null) && (
        <PiStatGrid>
          <PiStat
            label="Applications"
            value={formatCount(headline?.grandTotal)}
            loading={tilesLoading}
          />
          <PiStat
            label="Scholarships applied to"
            value={formatCount(headline?.withApplications)}
            {...(headline !== null ? { detail: `of ${formatCount(headline.listed)}` } : {})}
            loading={tilesLoading}
          />
          <PiStat
            label="Top scholarship"
            value={
              headline?.top != null ? (
                <span className="[overflow-wrap:anywhere]">{headline.top.displayName}</span>
              ) : (
                '—'
              )
            }
            {...(headline?.top != null
              ? {
                  detail: `${formatCount(headline.top.total)} · ${formatRate(
                    stageRate(headline.top.total, headline.grandTotal),
                  )}`,
                }
              : {})}
            loading={tilesLoading}
          />
        </PiStatGrid>
      )}

      <PiSection
        title="Scholarships"
        count={summary === null ? null : rows.length}
        actions={controls}
        flush
      >
        {body}
      </PiSection>
    </div>
  );
}
